import React from "react";
import {Ban,ChevronRight,Download,ExternalLink,FileText,ImageIcon,Info,MapPin,MessageCircle,Music2,PlayCircle,Reply,Smile,UserRound} from "lucide-react";

import MediaUnavailable from "./MediaUnavailable";

/* =========================================================
   TEXT MESSAGE
========================================================= */

function TextMessage({ payload }) {
  return (
    <div className="max-w-full">
      <p className="whitespace-pre-wrap break-words text-[13.5px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload?.body || ""}</p>
    </div>
  );
}

/* =========================================================
   IMAGE MESSAGE
========================================================= */

function ImageMessage({ payload }) {
  if (!payload?.link) {
    return <MediaUnavailable icon={ImageIcon} title="Image" description="Image unavailable" />;
  }

  return (
    <div className="w-[260px] max-w-full overflow-hidden rounded-[8px]">
      <div className="group/media relative overflow-hidden rounded-[7px] bg-app-bg dark:bg-dark-soft-blue">
        <img src={payload.link} alt={payload.caption || "Image"} className="block max-h-[300px] w-full object-cover transition-transform duration-300 group-hover/media:scale-[1.015]" />

        <a href={payload.link} target="_blank" rel="noopener noreferrer" download className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/45 text-white opacity-0 backdrop-blur-sm transition-all duration-200 hover:bg-black/65 group-hover/media:opacity-100" title="Download">
          <Download size={14} />
        </a>
      </div>

      {payload.caption && (
        <p className="px-1 pb-1 pt-2 text-[13px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload.caption}</p>
      )}
    </div>
  );
}

/* =========================================================
   VIDEO MESSAGE
========================================================= */

function VideoMessage({ payload }) {
  if (!payload?.link) {
    return <MediaUnavailable icon={PlayCircle} title="Video" description="Video unavailable" />;
  }

  return (
    <div className="w-[280px] max-w-full">
      <div className="overflow-hidden rounded-[7px] bg-black">
        <video controls preload="metadata" className="block max-h-[300px] w-full object-contain">
          <source src={payload.link} />
          Your browser does not support video playback.
        </video>
      </div>

      {payload.caption && (
        <p className="px-1 pb-1 pt-2 text-[13px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload.caption}</p>
      )}

      <a href={payload.link} target="_blank" rel="noopener noreferrer" download className="ml-1 mt-1 inline-flex items-center gap-1.5 text-[11px] font-medium text-text-secondary transition hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-primary">
        <Download size={13} />
        Download video
      </a>
    </div>
  );
}

/* =========================================================
   AUDIO MESSAGE
========================================================= */

function AudioMessage({ payload }) {
  if (!payload?.link) {
    return <MediaUnavailable icon={Music2} title="Audio" description="Audio unavailable" />;
  }

  return (
    <div className="w-[280px] max-w-full">
      <div className="flex items-center gap-3 rounded-[9px] border border-border-light bg-app-bg px-3 py-2.5 dark:border-dark-border dark:bg-dark-soft-blue">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm dark:bg-dark-primary">
          <Music2 size={17} />
        </div>

        <div className="min-w-0 flex-1">
          <audio controls className="h-8 w-full">
            <source src={payload.link} />
            Your browser does not support audio playback.
          </audio>
        </div>
      </div>

      <a href={payload.link} target="_blank" rel="noopener noreferrer" download className="ml-1 mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-medium text-text-secondary transition hover:text-primary dark:text-dark-text-secondary dark:hover:text-dark-primary">
        <Download size={13} />
        Download audio
      </a>
    </div>
  );
}

/* =========================================================
   DOCUMENT MESSAGE
========================================================= */

function DocumentMessage({ payload }) {
  return (
    <div className="w-[280px] max-w-full">
      <div className="overflow-hidden rounded-[9px] border border-border-light bg-app-bg dark:border-dark-border dark:bg-dark-card">
        <div className="flex items-center gap-3 px-3 py-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[8px] bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
            <FileText size={20} strokeWidth={1.8} />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-[12px] font-medium text-text-primary dark:text-dark-text-primary">{payload?.filename || "Document"}</p>
            <p className="mt-0.5 text-[10px] text-text-muted dark:text-dark-text-muted">Document</p>
          </div>

          {payload?.link && (
            <a href={payload.link} target="_blank" rel="noopener noreferrer" download className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-text-secondary transition hover:bg-theme-50 hover:text-primary dark:text-dark-text-secondary dark:hover:bg-dark-soft-blue dark:hover:text-dark-primary" title="Download">
              <Download size={15} />
            </a>
          )}
        </div>

        {payload?.caption && (
          <div className="border-t border-border-light px-3 py-2 dark:border-dark-border">
            <p className="text-[12px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload.caption}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   REACTION MESSAGE
========================================================= */

function ReactionMessage({ payload }) {
  return (
    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-border-light bg-card-bg shadow-sm dark:border-dark-border dark:bg-dark-card">
      <span className="text-[26px] leading-none">{payload?.emoji || "👍"}</span>
    </div>
  );
}

/* =========================================================
   STICKER MESSAGE
========================================================= */

function StickerMessage({ payload }) {
  if (!payload?.link) {
    return <MediaUnavailable icon={Smile} title="Sticker" description="Sticker unavailable" />;
  }

  return (
    <div className="flex items-center justify-center px-1 py-1">
      <img src={payload.link} alt="Sticker" loading="lazy" className="block h-auto max-h-[180px] w-auto max-w-[180px] object-contain" />
    </div>
  );
}

/* =========================================================
   CONTACT MESSAGE
========================================================= */

function ContactsMessage({ payload }) {
  const contacts = Array.isArray(payload) ? payload : [];

  if (!contacts.length) {
    return <MediaUnavailable icon={UserRound} title="Contact" description="Contact unavailable" />;
  }

  return (
    <div className="w-[275px] max-w-full overflow-hidden rounded-[10px] border border-border-light bg-card-bg dark:border-dark-border dark:bg-dark-card">
      {contacts.map((contact, index) => {
        const name = contact?.name?.formatted_name || `${contact?.name?.first_name || ""} ${contact?.name?.last_name || ""}`.trim() || "Contact";
        const phone = contact?.phones?.[0]?.phone;

        return (
          <div key={index} className={`flex items-center gap-3 px-3 py-3 ${index !== contacts.length - 1 ? "border-b border-border-light dark:border-dark-border" : ""}`}>
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-theme-50 text-primary dark:bg-dark-soft-blue dark:text-dark-primary">
              <UserRound size={18} />
            </div>

            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold text-text-primary dark:text-dark-text-primary">{name}</p>

              {phone && <p className="mt-0.5 text-[11px] text-text-muted dark:text-dark-text-muted">{phone}</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* =========================================================
   LOCATION MESSAGE
========================================================= */

function LocationMessage({ payload }) {
  const latitude = payload?.latitude;
  const longitude = payload?.longitude;

  const mapsUrl = latitude && longitude ? `https://www.google.com/maps?q=${latitude},${longitude}` : null;

  return (
    <div className="w-[275px] max-w-full overflow-hidden rounded-[10px] border border-border-light bg-card-bg dark:border-dark-border dark:bg-dark-card">
      <div className="relative flex h-[125px] items-center justify-center overflow-hidden bg-theme-50 dark:bg-dark-soft-blue">
        <div className="absolute inset-0 opacity-30">
          <div className="h-full w-full bg-[radial-gradient(circle_at_center,_#147db5_1px,_transparent_1px)] [background-size:14px_14px] dark:bg-[radial-gradient(circle_at_center,_#5bbce8_1px,_transparent_1px)]" />
        </div>

        <div className="relative flex h-12 w-12 items-center justify-center rounded-full bg-white text-primary shadow-md dark:bg-dark-card dark:text-dark-primary">
          <MapPin size={23} strokeWidth={2} />
        </div>
      </div>

      <div className="p-3">
        <p className="text-[13px] font-semibold text-text-primary dark:text-dark-text-primary">{payload?.name || "Location"}</p>

        {payload?.address && <p className="mt-1 text-[11px] leading-4 text-text-muted dark:text-dark-text-muted">{payload.address}</p>}

        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary transition hover:text-theme-700 dark:text-dark-primary dark:hover:text-theme-300">
            <MapPin size={13} />
            Open in Maps
            <ExternalLink size={10} />
          </a>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   INTERACTIVE MESSAGE
========================================================= */

function InteractiveMessage({ message, payload, onInteractiveClick }) {
  const interactiveType = payload?.type;

  if (interactiveType === "button") {
    const buttons = payload?.action?.buttons ?? [];

    return (
      <div className="w-[270px] max-w-full">
        <div className="space-y-2">
          {payload?.header?.type === "image" && <ImageMessage payload={payload.header.image} />}
          {payload?.header?.type === "document" && <DocumentMessage payload={payload.header.document} />}
          {payload?.header?.text && <p className="text-[13px] font-semibold text-text-primary dark:text-dark-text-primary">{payload.header.text}</p>}

          {payload?.body?.text && <p className="whitespace-pre-wrap text-[13.5px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload.body.text}</p>}

          {payload?.footer?.text && <p className="text-[10px] leading-4 text-text-muted dark:text-dark-text-muted">{payload.footer.text}</p>}
        </div>

        {buttons.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-[9px] border border-border-light bg-card-bg dark:border-dark-border dark:bg-dark-card">
            {buttons.map((button, index) => {
              const title = button?.reply?.title || "Option";

              return (
                <button key={button?.reply?.id || index} type="button" onClick={() => onInteractiveClick?.({ message, button })} className={`group/button flex w-full items-center justify-center gap-1 px-3 py-[9px] text-[12px] font-medium text-primary transition hover:bg-theme-50 dark:text-dark-primary dark:hover:bg-dark-soft-blue ${index !== buttons.length - 1 ? "border-b border-border-light dark:border-dark-border" : ""}`}>
                  <span>{title}</span>

                  <ChevronRight size={13} className="opacity-0 transition group-hover/button:translate-x-0.5 group-hover/button:opacity-100" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (interactiveType === "list") {
    const sections = payload?.action?.sections ?? [];

    return (
      <div className="w-[290px] max-w-full">
        {payload?.header?.text && <p className="mb-1 text-[13px] font-semibold text-text-primary dark:text-dark-text-primary">{payload.header.text}</p>}

        {payload?.body?.text && <p className="whitespace-pre-wrap text-[13.5px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload.body.text}</p>}

        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mt-3 overflow-hidden rounded-[9px] border border-border-light bg-card-bg dark:border-dark-border dark:bg-dark-card">
            {section?.title && (
              <div className="border-b border-border-light bg-app-bg px-3 py-2 dark:border-dark-border dark:bg-dark-soft-blue">
                <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">{section.title}</p>
              </div>
            )}

            {(section?.rows ?? []).map((row, rowIndex) => (
              <button key={row?.id || rowIndex} type="button" onClick={() => onInteractiveClick?.({ message, row })} className="group/row flex w-full items-center gap-2 border-b border-border-light px-3 py-2.5 text-left transition last:border-b-0 hover:bg-theme-50 dark:border-dark-border dark:hover:bg-dark-soft-blue">
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-primary dark:text-dark-primary">{row?.title}</p>

                  {row?.description && <p className="mt-0.5 line-clamp-2 text-[10px] leading-4 text-text-muted dark:text-dark-text-muted">{row.description}</p>}
                </div>

                <ChevronRight size={14} className="shrink-0 text-text-muted transition group-hover/row:translate-x-0.5 group-hover/row:text-primary dark:text-dark-text-muted dark:group-hover/row:text-dark-primary" />
              </button>
            ))}
          </div>
        ))}

        {payload?.footer?.text && <p className="mt-2 text-[10px] leading-4 text-text-muted dark:text-dark-text-muted">{payload.footer.text}</p>}
      </div>
    );
  }

  return <p className="text-[13.5px] leading-[1.45] text-text-primary dark:text-dark-text-primary">{payload?.body?.text || ""}</p>;
}

/* =========================================================
   INTERACTIVE REPLY MESSAGE
========================================================= */

function InteractiveReplyMessage({ payload }) {
  const replyType = payload?.type;

  if (replyType === "button_reply") {
    const title = payload?.button_reply?.title || "Selected option";

    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 rounded-[8px] bg-theme-50 px-3 py-2 ring-1 ring-primary/5 dark:bg-dark-soft-blue">
          <MessageCircle size={14} className="shrink-0 text-primary dark:text-dark-primary" />
          <span className="text-[12.5px] font-medium text-text-primary dark:text-dark-text-primary">{title}</span>
        </div>
      </div>
    );
  }

  if (replyType === "list_reply") {
    const title = payload?.list_reply?.title || "Selected option";
    const description = payload?.list_reply?.description;

    return (
      <div className="min-w-[190px] max-w-[280px]">
        <div className="rounded-[8px] bg-theme-50 px-3 py-2.5 ring-1 ring-primary/5 dark:bg-dark-soft-blue">
          <div className="flex items-start gap-2">
            <MessageCircle size={14} className="mt-0.5 shrink-0 text-primary dark:text-dark-primary" />

            <div className="min-w-0">
              <p className="text-[12.5px] font-medium text-text-primary dark:text-dark-text-primary">{title}</p>

              {description && <p className="mt-0.5 text-[10.5px] leading-4 text-text-muted dark:text-dark-text-muted">{description}</p>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <p className="text-[13px] text-text-muted dark:text-dark-text-muted">{payload?.button_reply?.title || payload?.list_reply?.title || "Interactive reply"}</p>;
}

/* =========================================================
   BUTTON REPLY  (customer tapped a template quick-reply button)
========================================================= */

function ButtonReplyMessage({ payload }) {
  const title = payload?.text || payload?.payload || "Button reply";
  return (
    <div className="flex items-center gap-2 rounded-[8px] bg-theme-50 px-3 py-2 ring-1 ring-primary/5 dark:bg-dark-soft-blue">
      <Reply size={14} className="shrink-0 text-primary dark:text-dark-primary" />
      <span className="text-[12.5px] font-medium text-text-primary dark:text-dark-text-primary">
        {title}
      </span>
    </div>
  );
}

/* =========================================================
   SYSTEM MESSAGE  (number changed, security notification, etc.)
========================================================= */

function SystemMessage({ payload }) {
  return (
    <div className="flex items-center gap-1.5 text-text-muted dark:text-dark-text-muted">
      <Info size={13} className="shrink-0" />
      <span className="text-[12px] italic">{payload?.body || "System notification"}</span>
    </div>
  );
}

/* =========================================================
   UNSUPPORTED  (types WhatsApp can send that we don't render inline)
========================================================= */

function UnsupportedMessage({ type }) {
  const label = type && type !== "unknown" ? `${type} message` : "This message";
  return (
    <div className="flex items-center gap-1.5 text-text-muted dark:text-dark-text-muted">
      <Ban size={14} className="shrink-0" />
      <span className="text-[12px] italic">
        {label} can’t be shown here — open WhatsApp to view it.
      </span>
    </div>
  );
}

/* =========================================================
   TEMPLATE MESSAGE
========================================================= */

function TemplateMessage({ payload }) {
  const components = payload?.components ?? [];

  const headerComponent = components.find((component) => component?.type?.toLowerCase() === "header");
  const bodyComponent = components.find((component) => component?.type?.toLowerCase() === "body");
  const footerComponent = components.find((component) => component?.type?.toLowerCase() === "footer");
  const parameters = bodyComponent?.parameters ?? [];

  return (
    <div className="w-[285px] max-w-full overflow-hidden rounded-[11px] border border-border-light bg-card-bg shadow-[0_2px_8px_rgba(15,23,42,0.05)] transition-colors duration-200 dark:border-dark-border dark:bg-dark-card dark:shadow-[0_2px_10px_rgba(0,0,0,0.18)]">
      <div className="border-b border-border-light bg-theme-50/60 px-3 py-2.5 dark:border-dark-border dark:bg-dark-soft-blue/70">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-theme-100 bg-white text-primary shadow-[0_2px_5px_rgba(20,125,181,0.08)] dark:border-dark-border dark:bg-dark-card dark:text-dark-primary">
            <MessageCircle size={15} strokeWidth={1.9} />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">Template</p>
            <p className="mt-0.5 truncate text-[12px] font-semibold text-text-primary dark:text-dark-text-primary">{payload?.name || "WhatsApp Template"}</p>
          </div>
        </div>
      </div>

      {headerComponent && (
        <div className="border-b border-border-light px-3 py-2.5 dark:border-dark-border">
          <p className="text-[9px] font-semibold uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">Header</p>
          <p className="mt-1 text-[12px] font-medium text-text-primary dark:text-dark-text-primary">{headerComponent?.parameters?.[0]?.text || headerComponent?.text || headerComponent?.format || "-"}</p>
        </div>
      )}

      <div className="px-3 py-3">
        <p className="whitespace-pre-wrap break-words text-[13px] leading-[1.5] text-text-primary dark:text-dark-text-primary">{bodyComponent?.text || payload?.body || "Template message"}</p>

        {parameters.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {parameters.map((parameter, index) => (
              <div key={index} className="rounded-[8px] border border-border-light bg-app-bg px-2.5 py-2 transition-colors duration-200 dark:border-dark-border dark:bg-dark-soft-blue">
                <p className="text-[9px] font-medium uppercase tracking-[0.07em] text-text-muted dark:text-dark-text-muted">Variable {index + 1}</p>
                <p className="mt-0.5 break-words text-[11px] font-medium text-text-secondary dark:text-dark-text-secondary">{parameter?.text || parameter?.type || "-"}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {footerComponent?.text && (
        <div className="border-t border-border-light px-3 py-2 dark:border-dark-border">
          <p className="text-[10px] leading-4 text-text-muted dark:text-dark-text-muted">{footerComponent.text}</p>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-border-light bg-app-bg/60 px-3 py-1.5 dark:border-dark-border dark:bg-dark-soft-blue/40">
        <span className="text-[8px] font-medium uppercase tracking-[0.08em] text-text-muted dark:text-dark-text-muted">Language</span>
        <span className="rounded-full bg-theme-50 px-2 py-0.5 text-[9px] font-semibold text-primary dark:bg-dark-soft-blue dark:text-dark-primary">{payload?.language?.code || "en"}</span>
      </div>
    </div>
  );
}

/* =========================================================
   EXPORTS
========================================================= */

export {
  TextMessage,
  ImageMessage,
  VideoMessage,
  AudioMessage,
  DocumentMessage,
  ReactionMessage,
  StickerMessage,
  ContactsMessage,
  LocationMessage,
  InteractiveMessage,
  InteractiveReplyMessage,
  ButtonReplyMessage,
  SystemMessage,
  UnsupportedMessage,
  TemplateMessage,
};